import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClientConfigService, shouldCheckAgeRateToken, shouldCheckKidsToken } from 'osn-shared';
import { Observable, tap } from 'rxjs';
import {
  MpxGenreResponse,
  MpxGuidAndIdResponse,
  MpxGuidResponse,
  MpxProgramResponse,
  MpxSeasonContentResponse,
} from './data/mpx-program-response';
import { MpxResponse } from './data/mpx-response';
import { MpxHttpParamsBuilder } from './util/mpx-http-params-builder';

@Injectable({ providedIn: 'root' })
export class ContentMpxService {
  constructor(private readonly httpClient: HttpClient, private readonly clientConfigService: ClientConfigService) {}

  private getAllProgramFeedMpxRequestNoAgeRateFiltering$<T>(
    paramsBuilder: MpxHttpParamsBuilder = new MpxHttpParamsBuilder()
  ): Observable<MpxResponse<T>> {
    return this.httpClient.get<MpxResponse<T>>(this.clientConfigService.mpxAllProgramsFeedUrl, {
      params: paramsBuilder.build(),
    });
  }

  private getAllProgramFeedMpxRequest$<T>(paramsBuilder: MpxHttpParamsBuilder = new MpxHttpParamsBuilder()): Observable<MpxResponse<T>> {
    return this.httpClient.get<MpxResponse<T>>(this.clientConfigService.mpxAllProgramsFeedUrl, {
      params: paramsBuilder.build(),
      context: new HttpContext().set(shouldCheckKidsToken, true).set(shouldCheckAgeRateToken, true),
    });
  }

  private getAllSeasonFeedMpxRequest$(
    paramsBuilder: MpxHttpParamsBuilder = new MpxHttpParamsBuilder()
  ): Observable<MpxResponse<MpxSeasonContentResponse>> {
    return this.httpClient
      .get<MpxResponse<MpxSeasonContentResponse>>(this.clientConfigService.mpxAllSeasonsFeedUrl, {
        params: paramsBuilder.build(),
      })
      .pipe(
        tap((result) => {
          // This needs to be manually set, as seasons are in general not considered as program on MPX but we need to handle it
          result.entries.forEach((it) => (it.programType = 'season'));
        })
      );
  }

  private getAllProgramFeedIdMpxRequest$<T>(
    ids: string[],
    paramsBuilder: MpxHttpParamsBuilder = new MpxHttpParamsBuilder()
  ): Observable<MpxResponse<T>> {
    return this.httpClient.get<MpxResponse<T>>(`${this.clientConfigService.mpxAllProgramsFeedUrl}/${ids.join(',')}`, {
      params: paramsBuilder.build(),
      context: new HttpContext().set(shouldCheckKidsToken, true).set(shouldCheckAgeRateToken, true),
    });
  }

  private getCustomFeedMpxRequest$<T>(
    feedId: string,
    paramsBuilder: MpxHttpParamsBuilder = new MpxHttpParamsBuilder()
  ): Observable<MpxResponse<T>> {
    return this.httpClient.get<MpxResponse<T>>(`${this.clientConfigService.mpxFeedUrlPrefix}${feedId}`, {
      params: paramsBuilder.build(),
      context: new HttpContext().set(shouldCheckKidsToken, true).set(shouldCheckAgeRateToken, true),
    });
  }

  private getCustomFeedGuids$(feedId: string): Observable<MpxResponse<MpxGuidResponse>> {
    const params = new MpxHttpParamsBuilder().append('fields', 'guid').build();
    return this.httpClient.get<MpxResponse<MpxGuidResponse>>(`${this.clientConfigService.mpxFeedUrlPrefix}${feedId}`, { params });
  }

  public getAllSeriesAndMovies$(): Observable<MpxResponse<MpxProgramResponse>> {
    return this.getAllProgramFeedMpxRequest$<MpxProgramResponse>();
  }

  public getFeed$(feedId: string): Observable<MpxResponse<MpxProgramResponse>> {
    return this.getCustomFeedMpxRequest$<MpxProgramResponse>(feedId);
  }

  public getFeedGuids$(feedId: string): Observable<MpxResponse<MpxGuidResponse>> {
    return this.getCustomFeedGuids$(feedId);
  }

  public getByGuids$(guids: string[]): Observable<MpxResponse<MpxProgramResponse>> {
    return this.getAllProgramFeedMpxRequestNoAgeRateFiltering$<MpxProgramResponse>(
      new MpxHttpParamsBuilder().append('byGuid', guids.join('|'))
    );
  }

  public getArabicProgramByGuid$(guid: string): Observable<MpxResponse<MpxProgramResponse>> {
    return this.getAllProgramFeedMpxRequestNoAgeRateFiltering$<MpxProgramResponse>(
      new MpxHttpParamsBuilder().append('byGuid', guid).append('lang', 'ar')
    );
  }

  public getGuidsByIds$(ids: string[]): Observable<MpxResponse<MpxGuidAndIdResponse> | MpxGuidAndIdResponse> {
    return this.getAllProgramFeedIdMpxRequest$<MpxGuidAndIdResponse>(ids, new MpxHttpParamsBuilder().append('fields', 'guid,id'));
  }

  public getEpisodesBySeriesId$(seriesId: string): Observable<MpxResponse<MpxGuidResponse>> {
    return this.getAllProgramFeedMpxRequest$<MpxGuidResponse>(
      new MpxHttpParamsBuilder().append('byProgramType', 'episode').append('bySeriesId', seriesId).append('fields', 'guid')
    );
  }

  public getSeasonsBySeriesId$(seriesId: string): Observable<MpxResponse<MpxSeasonContentResponse>> {
    return this.getAllSeasonFeedMpxRequest$(new MpxHttpParamsBuilder().append('bySeriesId', seriesId));
  }

  public getGuidsByCastId$(castId: string): Observable<MpxResponse<MpxGuidResponse>> {
    return this.getAllProgramFeedMpxRequest$<MpxGuidResponse>(
      new MpxHttpParamsBuilder().append('byCredits', `byPersonId=${castId}`).append('fields', 'guid')
    );
  }

  public getGuidsByGenre$(genre: string): Observable<MpxResponse<MpxGuidResponse>> {
    return this.getAllProgramFeedMpxRequest$<MpxGuidResponse>(
      new MpxHttpParamsBuilder().append('byTags', `genre:${genre}`).append('fields', 'guid').append('byProgramType', 'series|movie')
    );
  }

  public getMpxGenres$(): Observable<MpxResponse<MpxGenreResponse>> {
    return this.httpClient.get<MpxResponse<MpxGenreResponse>>(this.clientConfigService.mpxAllGenresFeedUrl, {
      params: new MpxHttpParamsBuilder().build(),
    });
  }
}
